/**
 * §P.9 fase 4 · Cómo va el club frente a los que se le parecen.
 *
 * ## Con menos de cinco clubes en el tramo, no hay comparativa
 *
 * `422 benchmark_not_anonymous`, y **eso no es un error**: con cuatro, cualquiera de
 * los cuatro puede despejar los datos de los otros tres restando los suyos de la
 * media. La regla vive dentro de la vista de la base de datos para que ningún
 * endpoint pueda saltársela.
 *
 * Aquí se lee como un estado y se explica, porque es el estado normal al principio y
 * porque la explicación es un argumento a favor: un club que lee «no te comparamos
 * porque se sabría de quién son los datos» entiende que sus propios datos están
 * igual de protegidos.
 *
 * ## Más no siempre es mejor
 *
 * Más alumnos sí; más bajas, no. Pintar en verde toda diferencia positiva le diría a
 * un club que va bien justo cuando se le está yendo la gente.
 *
 * ## Y no usa la envoltura común de los informes
 *
 * Su sobre no es `rows`/`totals` y no tiene `?format=csv`. Meterlo en la envoltura
 * habría obligado a hacerla genérica para un solo caso.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { cuenta, euros, numero } from '@/shared/ui/formato';
import {
  comoSeEscribe,
  etiquetaDeMetrica,
  mejorEsMenos,
  type Comparativa as Datos,
} from '@/club/informes/tipos';

/** Cada métrica con su cara: dinero, porcentaje o cuenta. */
function escribir(metrica: string, valor: number): string {
  const como = comoSeEscribe(metrica);
  if (como === 'euros') return euros(Math.round(valor));
  if (como === 'porcentaje') return `${Math.round(valor * 100)} %`;
  return numero(valor, valor % 1 === 0 ? 0 : 1);
}

export function Comparativa({ club }: { club: string }) {
  const datos = useQuery({
    queryKey: ['comparativa', club],
    /*
     * Un `422` aquí es un estado, no un fallo, así que se devuelve `null` en vez de
     * lanzar. No se reintenta: la respuesta no va a cambiar hasta que haya cinco
     * clubes en el tramo, y reintentar sería pedir cuatro veces lo mismo.
     */
    retry: false,
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .reports.benchmark.get();
      if (error) {
        const p = aProblema(error);
        if (p.code === 'benchmark_not_anonymous') return null;
        throw new ErrorDeApi(p);
      }
      return data as unknown as Datos;
    },
  });

  if (datos.isPending) {
    return <Cargando filas={4} etiqueta="Cargando la comparativa" />;
  }

  if (datos.isError) {
    return (
      <PanelError
        problema={aProblema(datos.error)}
        reintentar={() => void datos.refetch()}
      />
    );
  }

  return (
    <section>
      <h1>Comparativa</h1>

      {datos.data === null ? (
        <>
          <p className="tenue">
            Todavía no hay bastantes clubes parecidos al tuyo para poder
            comparar.
          </p>
          <div className="hueco">
            <h2>Hacen falta cinco, y no es una cifra caprichosa</h2>
            <p>
              Con cuatro clubes en un grupo, cualquiera de los cuatro puede
              despejar los datos de los otros tres restando los suyos de la
              media. <strong>Dejaría de ser anónimo.</strong>
            </p>
            <p>
              Es la misma regla que protege los tuyos: cuando aparezcas en la
              media de otro club, tampoco se sabrá que eres tú.
            </p>
          </div>
        </>
      ) : (
        <>
          <p className="tenue">
            Frente a la media de{' '}
            <strong>{cuenta(datos.data.clubs, 'club', 'clubes')}</strong> de tu
            tamaño ({datos.data.size_band} alumnos).{' '}
            <strong>Nunca sabrás cuáles son</strong>, igual que ellos no saben
            que estás tú.
          </p>

          <div className="tabla-envoltura">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Qué</th>
                  <th className="num">Tú</th>
                  <th className="num">La media</th>
                  <th className="num">Diferencia</th>
                </tr>
              </thead>
              <tbody>
                {datos.data.rows.map((f) => {
                  /*
                   * El color mira la métrica, no el signo: más bajas es peor, y
                   * pintarlo en verde sería decirle al club que va bien justo
                   * cuando se le está yendo la gente.
                   */
                  const mejor = mejorEsMenos(f.metric)
                    ? f.difference < 0
                    : f.difference > 0;
                  const hayDiferencia = Math.abs(f.difference) > 0.0001;

                  return (
                    <tr key={f.metric}>
                      <td>{etiquetaDeMetrica(f.metric)}</td>
                      <td className="num">{escribir(f.metric, f.mine)}</td>
                      <td className="num">{escribir(f.metric, f.average)}</td>
                      <td className="num">
                        <span
                          className={
                            !hayDiferencia
                              ? 'tenue'
                              : mejor
                                ? 'pastilla pastilla-bien'
                                : 'pastilla pastilla-espera'
                          }
                        >
                          {f.difference > 0 ? '+' : ''}
                          {escribir(f.metric, f.difference)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <p className="tenue">
            Estar por debajo de la media en algo no es una mala noticia: es el
            sitio por donde empezar.
          </p>
        </>
      )}
    </section>
  );
}
